import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { isTurnstileEnabled, loadTurnstileScript, turnstileSiteKey } from '../utils/turnstile';

const TOKEN_TIMEOUT_MS = 30000;

/**
 * Cloudflare Turnstile in managed interaction-only mode: invisible until a
 * challenge is required for suspected bots.
 */
const TurnstileWidget = forwardRef(function TurnstileWidget(
  { onError, className = 'signup-split-turnstile' },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const resolveTokenRef = useRef(null);

  useImperativeHandle(ref, () => ({
    async getToken() {
      if (!isTurnstileEnabled()) {
        return null;
      }

      await loadTurnstileScript();
      const readyDeadline = Date.now() + TOKEN_TIMEOUT_MS;
      while (widgetIdRef.current == null && Date.now() < readyDeadline) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, 50);
        });
      }

      if (widgetIdRef.current == null) {
        throw new Error('turnstile_not_ready');
      }

      const widgetId = widgetIdRef.current;
      const existingToken = window.turnstile.getResponse(widgetId);
      if (existingToken && !window.turnstile.isExpired(widgetId)) {
        return existingToken;
      }

      return new Promise((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          resolveTokenRef.current = null;
          reject(new Error('turnstile_timeout'));
        }, TOKEN_TIMEOUT_MS);

        resolveTokenRef.current = (token) => {
          window.clearTimeout(timeoutId);
          resolveTokenRef.current = null;
          resolve(token);
        };

        window.turnstile.reset(widgetId);
      });
    },
  }));

  useEffect(() => {
    if (!isTurnstileEnabled() || !containerRef.current) {
      return undefined;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) {
          return;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: turnstileSiteKey,
          theme: 'light',
          callback: (token) => {
            if (resolveTokenRef.current) {
              resolveTokenRef.current(token);
            }
          },
          'error-callback': (errorCode) => {
            console.error('[Turnstile] error-callback:', errorCode, {
              sitekeyPrefix: turnstileSiteKey.slice(0, 12),
              hostname:
                typeof window !== 'undefined' ? window.location.hostname : undefined,
            });
            resolveTokenRef.current = null;
            onError?.(errorCode);
          },
          'expired-callback': () => {
            resolveTokenRef.current = null;
          },
        });
      })
      .catch((error) => {
        console.error('Turnstile load error:', error);
        onError?.('load_failed');
      });

    return () => {
      cancelled = true;
      resolveTokenRef.current = null;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onError]);

  if (!isTurnstileEnabled()) {
    return null;
  }

  return <div ref={containerRef} className={className} />;
});

export default TurnstileWidget;
